"use client";

import Link from "next/link";
import Image from "next/image";
import Script from "next/script";
import { createElement, useEffect, useRef, useState, type ComponentPropsWithoutRef, type MouseEvent } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

const StickyPricingHeader = dynamic(() => import("@/components/sticky-pricing-header"), { ssr: false });
const PricingComparison = dynamic(() => import("@/components/sections/pricing-comparison"), { ssr: false });

type MotionDivProps = ComponentPropsWithoutRef<"div"> & {
  animate?: unknown;
  transition?: unknown;
  whileHover?: unknown;
};

type MotionCircleProps = ComponentPropsWithoutRef<"circle"> & {
  animate?: unknown;
  transition?: unknown;
};

const motion = {
  div: ({ animate: _animate, transition: _transition, whileHover: _whileHover, ...props }: MotionDivProps) => <div {...props} />,
  circle: ({ animate: _animate, transition: _transition, ...props }: MotionCircleProps) => <circle {...props} />,
};

import { useSiteSettings } from "@/components/site-settings-provider";
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Globe2,
  LineChart,
  Link2,
  Minus,
  MousePointerClick,
  QrCode,
  Rocket,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Users,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IconRenderer } from "@/components/ui/icon-renderer";
import { getPublicPricing, resolvePublicPageUrl } from "@/lib/api";
import { SIGNUP_DASHBOARD_URL } from "@/lib/auth-urls";

type Content = Record<string, unknown>;

const iconMap = {
  Link2,
  QrCode,
  BarChart3,
  LineChart,
  Rocket,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Zap,
  Globe2,
  MousePointerClick,
} as const;

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}
function asList<T extends Record<string, unknown>>(value: unknown): T[] {
  return Array.isArray(value) ? value.filter((item): item is T => Boolean(item && typeof item === "object")) : [];
}

function normalizeSlugPart(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function resolveFeatureDetailHref(item: Record<string, unknown>) {
  const explicitHref = asString(item.href || item.url);
  if (explicitHref) {
    return resolvePublicPageUrl(explicitHref);
  }

  const title = asString(item.title || item.name);
  if (!title) {
    return "";
  }

  const aliases: Record<string, string> = {
    "informational-messages": "informational-messages",
    "coupons-promotions": "coupons-promotions",
    "social-proof-conversions": "social-proof-conversions",
    "lead-generation-tools": "popup-collectors",
    "multimedia-engagement": "multimedia-engagement",
    "feedback-interactions": "feedback-interactions",
    "countdown-timers": "countdown-timers",
    "social-sharing-engagement": "social-sharing-engagement",
    "cookie-notifications-compliance": "cookie-notifications",
    "customizable-notifications": "customizable-notifications",
  };

  const normalized = normalizeSlugPart(title);
  const slug = aliases[normalized] || normalized;
  return resolvePublicPageUrl(`/features/${slug}/`);
}

function describeCard(item: Record<string, unknown>) {
  const title = asString(item.title || item.name);
  const raw = asString(item.description).trim();
  if (raw && raw.toLowerCase() !== title.toLowerCase() && raw.length >= 20) {
    return raw;
  }

  const normalized = normalizeSlugPart(title);
  if (/coupon|promotion/.test(normalized)) {
    return "Launch time-sensitive offers with urgency and smart triggers to lift checkout conversion rates.";
  }
  if (/countdown|timer/.test(normalized)) {
    return "Create real urgency with evergreen or fixed-date countdowns that push high-intent visitors to act.";
  }
  if (/social-proof|review|trust/.test(normalized)) {
    return "Display real-time activity and trust signals to reduce hesitation and improve purchase confidence.";
  }
  if (/lead|collector|email/.test(normalized)) {
    return "Capture qualified leads with contextual forms, smart targeting, and instant CRM-ready submissions.";
  }
  if (/chat|whatsapp|engagement/.test(normalized)) {
    return "Open direct conversations at key moments to improve response speed and accelerate decision-making.";
  }
  if (/cookie|compliance/.test(normalized)) {
    return "Stay compliant with modern privacy expectations while keeping consent experiences clean and on-brand.";
  }

  return `Use ${title} to improve on-site engagement, guide user actions, and drive measurable conversion outcomes.`;
}

function getStableVariantIndex(seed: string, count: number) {
  if (count <= 1) return 0;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash % count;
}

function getCardCtaLabel(href: string, seed = "") {
  const normalizedHref = href.toLowerCase();
  const normalizedSeed = `${normalizedHref}|${seed.toLowerCase()}`;

  if (normalizedHref.includes("-vs-") || normalizedHref.includes("-alternative")) {
    const variants = ["See full breakdown", "Compare side-by-side", "Dive into details"];
    return variants[getStableVariantIndex(normalizedSeed, variants.length)];
  }
  if (normalizedHref.includes("/features/")) {
    const variants = ["Explore feature details", "View feature deep dive", "See how it works"];
    return variants[getStableVariantIndex(normalizedSeed, variants.length)];
  }
  if (normalizedHref.includes("/integrations/")) {
    const variants = ["View integration guide", "See setup details", "Explore connection steps"];
    return variants[getStableVariantIndex(normalizedSeed, variants.length)];
  }

  const variants = ["Learn more", "Discover details", "Explore now"];
  return variants[getStableVariantIndex(normalizedSeed, variants.length)];
}

/* ---------------- shared bits ---------------- */

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add("in");
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function parseNumber(input: string): { value: number; prefix: string; suffix: string; decimals: number } | null {
  if (!input) return null;
  const m = input.match(/^([^\d.-]*)([\d,.]+)\s*([^\d]*)$/);
  if (!m) return null;
  const numStr = m[2].replace(/,/g, "");
  const value = parseFloat(numStr);
  if (Number.isNaN(value)) return null;
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return { value, prefix: m[1] || "", suffix: m[3] || "", decimals };
}

function AnimatedNumber({ raw, className }: { raw: string; className?: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [shown, setShown] = useState(raw);
  const parsed = parseNumber(raw);

  useEffect(() => {
    if (!parsed || !ref.current) {
      setShown(raw);
      return;
    }
    const el = ref.current;
    let started = false;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            const dur = 1800;
            const start = performance.now();
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / dur);
              const eased = 1 - Math.pow(1 - t, 3);
              const v = parsed.value * eased;
              const formatted =
                parsed.value >= 1000 && !parsed.suffix.match(/[KkMmBb%+]/)
                  ? Math.floor(v).toLocaleString()
                  : v.toFixed(parsed.decimals);
              setShown(`${parsed.prefix}${formatted}${parsed.suffix}`);
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [raw]);

  return <span ref={ref} className={className}>{parsed ? shown : raw}</span>;
}

function IconBubble({ iconKey = "Sparkles", tone = "default" }: { iconKey?: string; tone?: "default" | "violet" | "emerald" | "amber" }) {
  const Icon = iconMap[iconKey as keyof typeof iconMap];
  const toneClass = {
    default: "from-sky-500 via-blue-600 to-indigo-600 shadow-blue-500/30",
    violet: "from-fuchsia-500 via-violet-600 to-indigo-600 shadow-violet-500/30",
    emerald: "from-emerald-400 via-teal-500 to-cyan-600 shadow-emerald-500/30",
    amber: "from-amber-400 via-orange-500 to-rose-500 shadow-amber-500/30",
  }[tone];
  return (
    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${toneClass} text-white shadow-lg`}>
      {Icon ? <Icon className="h-5 w-5" /> : <IconRenderer iconKey={iconKey} className="h-5 w-5" emojiClassName="text-2xl leading-none" />}
    </div>
  );
}

function resolveCtaTheme(content: Content) {
  const key = asString(content.ctaTheme, "engagement").toLowerCase();
  const themes: Record<string, { buttonGradient: string; sectionGradient: string; shadow: string }> = {
    "lead-generation": {
      buttonGradient: "from-fuchsia-500 via-pink-600 to-rose-600",
      sectionGradient: "from-fuchsia-600 via-pink-600 to-rose-600",
      shadow: "shadow-fuchsia-900/30",
    },
    analytics: {
      buttonGradient: "from-sky-500 via-blue-600 to-cyan-600",
      sectionGradient: "from-sky-600 via-blue-600 to-cyan-600",
      shadow: "shadow-blue-900/30",
    },
    conversion: {
      buttonGradient: "from-orange-500 via-amber-600 to-red-600",
      sectionGradient: "from-orange-600 via-amber-600 to-red-600",
      shadow: "shadow-orange-900/30",
    },
    engagement: {
      buttonGradient: "from-cyan-500 via-blue-600 to-violet-600",
      sectionGradient: "from-blue-600 via-violet-600 to-fuchsia-600",
      shadow: "shadow-violet-900/30",
    },
  };

  return themes[key] || themes.engagement;
}

function CtaButtons({ content, align = "center" }: { content: Content; align?: "center" | "left" }) {
  const primary = (content.primaryCta || content.ctaPrimary) as Content | undefined;
  const secondary = (content.secondaryCta || content.ctaSecondary) as Content | undefined;
  const theme = resolveCtaTheme(content);
  return (
    <div className={`mt-8 flex flex-wrap gap-3 ${align === "center" ? "justify-center" : ""}`}>
      <Link href={resolvePublicPageUrl(asString(primary?.url, SIGNUP_DASHBOARD_URL))}>
        <Button variant="gradient" size="lg" className={`glow-md cta-aurora-button bg-[length:220%_220%] ${theme.buttonGradient}`}>
          {asString(primary?.text, "Create Free Account")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </Link>
      {secondary || asString((content as Content).secondaryCta) ? (
        <Link href={resolvePublicPageUrl(asString(secondary?.url, SIGNUP_DASHBOARD_URL))}>
          <Button variant="outline" size="lg" className="cta-secondary-button">{asString(secondary?.text, "Start Free Trial")}</Button>
        </Link>
      ) : null}
    </div>
  );
}

function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ---------------- Animated Headline ---------------- */

function AnimatedHeadline({ base, animatedWords, animationType, highlight, useRainbow, rainbowGradient }: { base: string; animatedWords?: string[]; animationType?: string; highlight?: string; useRainbow?: boolean; rainbowGradient?: string }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!animatedWords || animatedWords.length === 0) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % animatedWords.length);
        setVisible(true);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, [animatedWords]);

  const animated = animatedWords && animatedWords.length ? animatedWords[index] : "";

  const prefersReduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const spectrumClass = rainbowGradient ? "animated-spectrum-text" : "rainbow-text";
  const wrapperStyle = rainbowGradient ? ({ ["--animated-spectrum" as any]: rainbowGradient } as any) : undefined;

  const renderAnimated = (text: string) => {
    if (prefersReduced || !text)
      return (
        <span className={useRainbow ? spectrumClass : undefined} style={useRainbow ? wrapperStyle : undefined}>
          {text}
        </span>
      );

    if (animationType === "typewriter") {
      return <TypewriterText text={text} active={visible} useRainbow={useRainbow} rainbowGradient={rainbowGradient} />;
    }

    if (animationType === "words") {
      return <WordByWordReveal text={text} active={visible} useRainbow={useRainbow} rainbowGradient={rainbowGradient} />;
    }

    if (animationType === "gradient") {
      return (
        <span
          className={`inline-block ${useRainbow ? spectrumClass : "rainbow-text"} transition-transform duration-400 ${
            visible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
          }`}
          style={useRainbow ? wrapperStyle : undefined}
        >
          {text}
        </span>
      );
    }

    // default: fade + slide
    return (
      <span
        className={`inline-block ${useRainbow ? spectrumClass : ""} transition-transform duration-400 ${
          visible ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
        }`}
        style={useRainbow ? wrapperStyle : undefined}
      >
        {text}
      </span>
    );
  };

  const hl = asString(highlight, "");
  if (hl && base.includes(hl)) {
    const [before, after] = base.split(hl);
    const animatedNode = renderAnimated(animated || hl);

    return (
      <span>
        {before}
        {animatedNode}
        {after}
      </span>
    );
  }

  // no highlight, append animated word — apply rainbow only to the animated word
  return (
    <span>
      {base} {renderAnimated(animated)}
    </span>
  );
}

function TypewriterText({ text, active, useRainbow, rainbowGradient }: { text: string; active: boolean; useRainbow?: boolean; rainbowGradient?: string }) {
  const [pos, setPos] = useState(0);
  useEffect(() => {
    if (!active) return;
    setPos(0);
    let raf: number | null = null;
    const start = performance.now();
    const dur = Math.max(600, text.length * 80);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      setPos(Math.floor(t * text.length));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, active]);

  const spectrumClass = rainbowGradient ? "animated-spectrum-text" : "rainbow-text";
  const style = rainbowGradient ? ({ ["--animated-spectrum" as any]: rainbowGradient } as any) : undefined;
  return (
    <span className={`inline-block ${useRainbow ? spectrumClass : ""}`} style={useRainbow ? style : undefined}>
      {text.slice(0, pos)}
    </span>
  );
}

function WordByWordReveal({ text, active, useRainbow, rainbowGradient }: { text: string; active: boolean; useRainbow?: boolean; rainbowGradient?: string }) {
  const words = text ? text.split(" ") : [];
  const [visibleCount, setVisibleCount] = useState(0);
  useEffect(() => {
    if (!active || words.length === 0) return;
    setVisibleCount(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setVisibleCount(i);
      if (i >= words.length) clearInterval(id);
    }, 180);
    return () => clearInterval(id);
  }, [text, active]);

  const spectrumClass = rainbowGradient ? "animated-spectrum-text" : "rainbow-text";
  const style = rainbowGradient ? ({ ["--animated-spectrum" as any]: rainbowGradient } as any) : undefined;

  return (
    <span aria-hidden>
      {words.map((w, idx) => (
        <span
          key={idx}
          className={`inline-block mr-1 transition-all duration-300 ${idx < visibleCount ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"} ${useRainbow ? spectrumClass : ""}`}
          style={useRainbow ? style : undefined}
        >
          {w}
        </span>
      ))}
    </span>
  );
}

/* ---------------- Right Visual (dynamic) ---------------- */

function RightVisual({ content }: { content: Content }) {
  const pathname = usePathname();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [pricingHeroPlans, setPricingHeroPlans] = useState<Array<Record<string, unknown>>>([]);
  const heroVisual = (content.heroVisual as Record<string, unknown>) || {};
  const visualType = asString(heroVisual.type, asString(content.visualType, "preset")).toLowerCase();
  const media = asString(heroVisual.src, asString(content.rightMedia));
  const poster = asString(heroVisual.poster);
  const heading = asString(content.heading, "Hero visual");
  const pageKey = `${pathname} ${asString(content.pageSlug)} ${asString(content.pageKey)} ${asString(content.sectionKey)} ${heading}`.toLowerCase();

  const inferPreset = () => {
    if (pathname === "/") return "home-engagement";
    if (pathname === "/integrations" || pathname.startsWith("/integrations/")) return "integrations-flow";
    if (pathname === "/comparison") return "comparison-listing";
    if (pathname.startsWith("/comparison/") || /\bvs\b|comparison/.test(pageKey)) return "comparison-detail";
    if (pathname === "/solutions") return "solutions-listing";
    if (pathname.startsWith("/solutions/")) {
      if (/marketers?/.test(pageKey)) return "solutions-marketers";
      if (/agenc(y|ies)/.test(pageKey)) return "solutions-agencies";
      if (/influencers?/.test(pageKey)) return "solutions-influencers";
      return "solutions-detail";
    }
    if (/popup|modal|collector bar|cta/.test(pageKey)) return "popup-widgets";
    if (/countdown|timer|urgency|flash sale/.test(pageKey)) return "countdown-timer";
    if (/social proof|conversion|activity|purchased/.test(pageKey)) return "social-proof";
    if (/whatsapp|chat|messag/.test(pageKey)) return "whatsapp-chat";
    if (/email|lead|subscriber|collector/.test(pageKey)) return "email-collector";
    if (/coupon|discount|promo|spin/.test(pageKey)) return "coupon-widget";
    if (/counter|statistics|live count|metrics/.test(pageKey)) return "live-counters";
    if (/video|media|youtube/.test(pageKey)) return "video-widget";
    if (/review|rating|trust|testimonial/.test(pageKey)) return "reviews-widget";
    if (/announcement|bar|banner/.test(pageKey)) return "announcement-bar";
    if (/pricing/.test(pageKey)) return "pricing";
    return "engagement-dashboard";
  };

  const legacyPresetMap: Record<string, string> = {
    "url-shortener": "popup-widgets",
    "bio-links": "whatsapp-chat",
    "qr-code-generator": "countdown-timer",
    analytics: "live-counters",
    "retargeting-pixel": "social-proof",
    "ab-testing": "coupon-widget",
    "link-rotator": "announcement-bar",
    "deep-links": "email-collector",
    "custom-domains": "reviews-widget",
    "link-management": "video-widget",
    dashboard: "engagement-dashboard",
  };

  const preset = asString(heroVisual.preset, asString(content.visualPreset, inferPreset()));
  const normalizedPreset = legacyPresetMap[preset] || preset;
  const visualTheme = asString(heroVisual.theme, asString(content.ctaTheme, "engagement")).toLowerCase();
  const visualLayout = asString(heroVisual.layout, "stacked").toLowerCase();
  const animationStyle = asString(heroVisual.animation, asString(content.animationType, "float")).toLowerCase();

  useEffect(() => {
    if (normalizedPreset !== "pricing") return;
    let mounted = true;
    getPublicPricing({ currency: "INR", billing: "monthly" })
      .then((res) => {
        if (!mounted) return;
        setPricingHeroPlans(Array.isArray(res.plans) ? (res.plans as unknown as Array<Record<string, unknown>>) : []);
      })
      .catch(() => {
        if (!mounted) return;
        setPricingHeroPlans([]);
      });

    return () => {
      mounted = false;
    };
  }, [normalizedPreset]);

  const shellClass = `relative overflow-hidden rounded-[28px] shadow-2xl [&_*]:!border-0 [&_*]:!ring-0 ${visualLayout === "compact" ? "max-w-[460px] ml-auto" : "w-full"}`;
  const panelThemeClass = visualTheme === "lead-generation"
    ? "bg-gradient-to-br from-rose-50 to-fuchsia-50 dark:from-fuchsia-950/40 dark:to-rose-950/30"
    : visualTheme === "analytics"
      ? "bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-950/40 dark:to-cyan-950/30"
      : visualTheme === "conversion"
        ? "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/30"
        : visualTheme === "communication"
          ? "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/35 dark:to-teal-950/30"
          : "bg-white dark:bg-slate-900/90";
  const panelClass = `relative rounded-2xl p-5 text-slate-900 dark:text-slate-100 ${panelThemeClass}`;
  const motionMultiplier = animationStyle === "minimal" ? 1.9 : animationStyle === "pulse" ? 0.75 : animationStyle === "ticker" ? 1.25 : 1;

  const handleTiltMove = (event: MouseEvent<HTMLDivElement>) => {
    const finePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;
    if (!finePointer) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    const maxTilt = 6;
    setTilt({
      x: (0.5 - py) * maxTilt,
      y: (px - 0.5) * maxTilt,
    });
  };

  const resetTilt = () => setTilt({ x: 0, y: 0 });

  if (visualType === "image" && media) {
    return (
      <div className="relative w-full">
        <Image src={media} alt={asString(heroVisual.alt, heading)} width={800} height={520} className="rounded-[28px] object-cover shadow-2xl" priority={false} />
      </div>
    );
  }

  if (visualType === "video" && media) {
    return (
      <div className="relative w-full overflow-hidden rounded-[28px] shadow-2xl [&_*]:!border-0 [&_*]:!ring-0">
        <video className="h-full w-full object-cover" autoPlay muted loop playsInline preload="metadata" poster={poster || undefined} aria-label={asString(heroVisual.alt, heading)}>
          <source src={media} />
        </video>
      </div>
    );
  }

  if (visualType === "lottie" && media) {
    return (
      <div className="relative w-full overflow-hidden rounded-[28px] shadow-2xl [&_*]:!border-0 [&_*]:!ring-0">
        <Script src="https://unpkg.com/@lottiefiles/lottie-player@2.0.12/dist/lottie-player.js" strategy="lazyOnload" />
        {createElement("lottie-player", {
          src: media,
          background: "transparent",
          speed: "1",
          loop: true,
          autoplay: true,
          style: { width: "100%", height: "100%", minHeight: "340px" },
          "aria-label": asString(heroVisual.alt, heading),
        })}
      </div>
    );
  }

  if (media && visualType === "media") {
    return (
      <div className="relative w-full">
        <Image src={media} alt={asString(heroVisual.alt, heading)} width={800} height={520} className="rounded-[28px] object-cover shadow-2xl" priority={false} />
      </div>
    );
  }

  if (normalizedPreset === "popup-widgets") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="rounded-xl border border-slate-200/35 p-3 text-xs text-slate-500 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-300">Homepage visitor reached 74% scroll depth</div>
          <motion.div className="mx-auto my-3 h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-violet-500" animate={{ y: [0, 3, 0], opacity: [0.9, 1, 0.9] }} transition={{ duration: 1.8, repeat: Infinity }} />
          <div className="rounded-xl border border-blue-200/35 bg-blue-50 p-3 text-sm font-semibold text-blue-700 dark:border-blue-700/30 dark:bg-blue-950/40 dark:text-blue-200">Slide-in popup: "Get 10% off your first order"</div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {["Trigger: Exit intent", "CTA: Claim coupon", "CVR +19%"].map((chip, i) => (
              <motion.div key={chip} className="rounded-full bg-violet-50 px-2 py-1 text-[11px] text-violet-700 dark:bg-violet-950/40 dark:text-violet-200" animate={{ y: [0, -4, 0] }} transition={{ duration: 2 + i * 0.4, repeat: Infinity }}>
                {chip}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "whatsapp-chat") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} flex items-center justify-center`}>
          <div className="w-[240px] rounded-[28px] border border-slate-200/35 bg-white p-3 shadow-xl dark:border-slate-700/35 dark:bg-slate-900">
            <div className="mx-auto h-12 w-12 rounded-full bg-gradient-to-br from-fuchsia-400 to-violet-500" />
            <div className="mt-2 text-center text-xs font-semibold text-slate-800 dark:text-slate-100">NotifyStudio Support</div>
            {["Hi! Need help selecting a widget?", "Typing...", "Shared: Conversion playbook"].map((item, i) => (
              <motion.div key={item} className="mt-2 rounded-lg border border-slate-200/35 p-2 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200" animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 1.6 + i * 0.2, repeat: Infinity }}>
                {item}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "countdown-timer") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid place-items-center`}>
          <div className="relative rounded-2xl border border-slate-200/35 bg-white p-5 dark:border-slate-700/35 dark:bg-slate-900">
            <div className="text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-300">Flash Sale Timer</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {["02", "14", "38"].map((unit, i) => (
                <motion.div key={unit + i} className="rounded-xl bg-slate-100 px-3 py-2 text-lg font-extrabold text-slate-900 dark:bg-slate-800 dark:text-white" animate={{ y: [0, -2, 0] }} transition={{ duration: 1.8 + i * 0.2, repeat: Infinity }}>
                  {unit}
                </motion.div>
              ))}
            </div>
            <motion.div className="pointer-events-none absolute inset-2 rounded-xl border-2 border-blue-500/50" animate={{ scale: [1, 1.05, 1], opacity: [0.45, 0.9, 0.45] }} transition={{ duration: 2.4, repeat: Infinity }} />
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "live-counters") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="rounded-2xl border border-slate-100/35 bg-gradient-to-br from-slate-50 to-blue-50/60 p-4 dark:border-slate-700/35 dark:from-slate-800 dark:to-blue-950/30">
            <div className="mt-5 flex h-40 items-end gap-2">
              {[36, 52, 48, 66, 74, 86, 92].map((height, index) => (
                <motion.div key={index} className="flex-1 rounded-t-xl bg-gradient-to-t from-blue-600 via-violet-500 to-fuchsia-400" animate={{ height: [height - 8, height, height - 4, height] }} transition={{ duration: 2 + index * 0.14, repeat: Infinity }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "social-proof") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid place-items-center`}>
          <div className="w-full space-y-2">
            {[
              "Someone in Mumbai started a free trial",
              "4 teams activated social proof in last hour",
              "New signup from SaaS growth campaign",
            ].map((line, i) => (
              <motion.div key={line} className="rounded-lg border border-emerald-200/40 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-700/35 dark:bg-emerald-950/30 dark:text-emerald-200" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.8 + i * 0.2, repeat: Infinity }}>
                {line}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (preset === "comparison-detail") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid grid-cols-2 gap-3`}>
          <div className="rounded-xl border border-slate-200/35 bg-slate-50 p-3 dark:border-slate-700/35 dark:bg-slate-800/70">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-300">Competitor</div>
            <div className="space-y-2">
              {["Basic links", "Limited analytics", "No QR depth"].map((line) => (
                <div key={line} className="rounded-md border border-slate-200/35 bg-white px-2 py-1 text-xs text-slate-500 dark:border-slate-700/35 dark:bg-slate-900 dark:text-slate-300">{line}</div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-blue-300/35 bg-blue-50 p-3 dark:border-blue-700/30 dark:bg-blue-950/30">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-700 dark:text-blue-200">NotifyStudio</div>
            <div className="space-y-2">
              {["Popup widgets", "Real-time counters", "Social proof + chat"].map((line, i) => (
                <motion.div key={line} className="rounded-md border border-blue-200/35 bg-white px-2 py-1 text-xs font-medium text-blue-700 dark:border-blue-700/25 dark:bg-slate-900 dark:text-blue-200" animate={{ x: [0, 3, 0] }} transition={{ duration: 1.8 + i * 0.2, repeat: Infinity }}>
                  {line}
                </motion.div>
              ))}
            </div>
          </div>
          <div className="col-span-2 rounded-xl border border-slate-200/35 p-3 dark:border-slate-700/35 dark:bg-slate-800/60">
            <div className="mb-2 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300"><span>Feature advantage</span><span>+42%</span></div>
            <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-violet-600" animate={{ width: ["62%", "76%", "71%"] }} transition={{ duration: 2.8, repeat: Infinity }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (preset === "comparison-listing") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid gap-3 sm:grid-cols-2`}>
          {["Bitly", "Rebrandly", "TinyURL", "Short.io"].map((name, i) => (
            <motion.div key={name} className="group rounded-xl border border-slate-200/35 bg-white p-3 dark:border-slate-700/35 dark:bg-slate-800/70" whileHover={{ y: -3 }}>
              <div className="flex items-center justify-between text-sm font-semibold text-slate-800 dark:text-slate-100">
                <span>{name}</span>
                <span className="text-blue-600 dark:text-blue-300">→ NotifyStudio</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700">
                <motion.div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-violet-600" animate={{ width: ["42%", "58%", "50%"] }} transition={{ duration: 2 + i * 0.2, repeat: Infinity }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (normalizedPreset === "solutions-listing") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid gap-3 sm:grid-cols-3`}>
          {[
            { name: "Marketing", color: "from-blue-500 to-cyan-500" },
            { name: "Agencies", color: "from-violet-500 to-fuchsia-500" },
            { name: "Influencers", color: "from-emerald-500 to-teal-500" },
          ].map((item, i) => (
            <motion.div key={item.name} className="rounded-xl border border-slate-200/35 bg-white p-3 dark:border-slate-700/35 dark:bg-slate-800/70" animate={{ y: [0, -2, 0] }} transition={{ duration: 2 + i * 0.3, repeat: Infinity }}>
              <div className={`mb-2 h-1.5 rounded-full bg-gradient-to-r ${item.color}`} />
              <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-300">{item.name}</div>
              <div className="mt-2 text-[11px] text-slate-600 dark:text-slate-300">Popup campaigns, social proof, and lead capture flows by industry.</div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  if (normalizedPreset === "solutions-marketers" || normalizedPreset === "solutions-detail") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="grid gap-3 md:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-xl border border-slate-200/35 bg-gradient-to-br from-slate-50 to-blue-50/60 p-3 dark:border-slate-700/35 dark:from-slate-800 dark:to-blue-950/30">
              <div className="mb-2 text-xs font-semibold text-slate-600 dark:text-slate-300">Campaign Performance</div>
              <div className="flex h-24 items-end gap-1.5">
                {[30, 42, 38, 60, 54, 72].map((h, i) => (
                  <motion.div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-blue-600 to-violet-500" animate={{ height: [h - 6, h, h - 3] }} transition={{ duration: 1.8 + i * 0.12, repeat: Infinity }} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {[
                "Intent popups",
                "Funnel reminders",
                "Lead collector overlays",
              ].map((item) => (
                <div key={item} className="rounded-lg border border-slate-200/35 bg-white px-3 py-2 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200">{item}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "solutions-agencies") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="grid gap-3 md:grid-cols-[120px_1fr]">
            <div className="rounded-xl border border-slate-200/35 bg-slate-50 p-2 text-xs text-slate-600 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-300">Client Campaigns</div>
            <div className="space-y-2">
              {["Client A - Exit popup", "Client B - Lead magnets", "Client C - Countdown launches"].map((item, i) => (
                <motion.div key={item} className="rounded-lg border border-slate-200/35 bg-white p-2 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200" animate={{ x: [0, i % 2 ? 4 : -4, 0] }} transition={{ duration: 2.2 + i * 0.2, repeat: Infinity }}>
                  {item}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "solutions-influencers") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} flex items-center justify-center`}>
          <div className="w-[220px] rounded-[24px] border border-slate-200/35 bg-white p-3 dark:border-slate-700/35 dark:bg-slate-900">
            <div className="mx-auto h-10 w-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-500" />
            <div className="mt-2 text-center text-xs font-semibold text-slate-800 dark:text-slate-100">Creator conversion stack</div>
            {["Announcement ribbon", "Coupon popup", "WhatsApp replies"].map((item, i) => (
              <motion.div key={item} className="mt-2 rounded-lg border border-slate-200/35 px-2 py-1.5 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200" animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 1.7 + i * 0.2, repeat: Infinity }}>
                {item}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "integrations-flow") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-300">Integration automations</div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {["Shopify", "HubSpot", "Slack"].map((platform, i) => (
              <motion.div key={platform} className="rounded-xl border border-slate-200/40 bg-white/80 px-2 py-3 text-center text-xs font-semibold text-slate-700 dark:border-slate-700/40 dark:bg-slate-800/70 dark:text-slate-200" animate={{ y: [0, -2, 0] }} transition={{ duration: (1.8 + i * 0.2) * motionMultiplier, repeat: Infinity }}>
                {platform}
              </motion.div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">NotifyStudio widgets</div>
            <motion.div className="text-blue-600" animate={{ x: [0, 6, 0] }} transition={{ duration: 1.8 * motionMultiplier, repeat: Infinity }}>→</motion.div>
            <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:bg-blue-950/40 dark:text-blue-200">CRM + Email + Chat sync</div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "home-engagement") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Popup modal",
              "Social proof",
              "Lead collector",
              "Countdown bar",
            ].map((name, i) => (
              <motion.div key={name} className="rounded-xl border border-slate-200/40 bg-white/80 p-3 dark:border-slate-700/40 dark:bg-slate-800/70" animate={{ y: [0, -2, 0] }} transition={{ duration: (1.9 + i * 0.2) * motionMultiplier, repeat: Infinity }}>
                <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{name}</div>
                <div className="mt-2 h-1.5 rounded-full bg-gradient-to-r from-cyan-500 via-blue-600 to-violet-500" />
              </motion.div>
            ))}
          </div>
          <div className="mt-3 rounded-xl border border-emerald-200/40 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-700/35 dark:bg-emerald-950/30 dark:text-emerald-200">
            Live: +27% conversion uplift from combined widget flows
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "coupon-widget") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid grid-cols-2 gap-3`}>
          <div className="rounded-xl border border-slate-200/35 p-3 text-xs dark:border-slate-700/35 dark:bg-slate-800/70">
            <div className="mb-2 font-semibold text-slate-800 dark:text-slate-100">Coupon A: 10%</div>
            <div className="h-20 rounded-lg bg-blue-100 dark:bg-blue-950/40" />
          </div>
          <div className="rounded-xl border border-slate-200/35 p-3 text-xs dark:border-slate-700/35 dark:bg-slate-800/70">
            <div className="mb-2 font-semibold text-slate-800 dark:text-slate-100">Coupon B: Spin to win</div>
            <div className="h-20 rounded-lg bg-violet-100 dark:bg-violet-950/40" />
          </div>
          <div className="col-span-2 rounded-xl border border-slate-200/35 p-3 dark:border-slate-700/35 dark:bg-slate-800/70">
            <div className="flex items-center justify-between text-xs text-slate-700 dark:text-slate-200">
              <span>Reveal: 48%</span><span>Claim: 52%</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-violet-600" animate={{ width: ["48%", "52%", "49%"] }} transition={{ duration: 2.8, repeat: Infinity }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "announcement-bar") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} relative grid place-items-center`}>
          <motion.div className="h-36 w-36 rounded-full border-2 border-dashed border-violet-300" animate={{ rotate: 360 }} transition={{ duration: 10, repeat: Infinity, ease: "linear" }} />
          <div className="absolute grid gap-2 text-xs">
            {["Free shipping today", "Webinar starts in 2h", "Launch week offer"].map((n) => <span key={n} className="rounded-full bg-violet-50 px-2 py-1 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200">{n}</span>)}
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "email-collector") {
    return (
      <div className={shellClass}>
        <div className={`${panelClass} grid grid-cols-[1fr_auto_1fr] items-center gap-3`}>
          <div className="rounded-xl border border-slate-200/35 bg-slate-50 p-3 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200">Email capture popup</div>
          <motion.div animate={{ x: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }} className="text-blue-600">→</motion.div>
          <div className="rounded-xl border border-blue-200/35 bg-blue-50 p-3 text-xs text-blue-700 dark:border-blue-700/30 dark:bg-blue-950/40 dark:text-blue-200">Subscriber segment synced</div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "reviews-widget") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="space-y-3 text-xs">
            <div className="rounded-lg border border-slate-200/35 p-2 text-slate-500 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-300">Customer review widget</div>
            <motion.div className="rounded-lg border border-blue-200/35 bg-blue-50 p-2 font-semibold text-blue-700 dark:border-blue-700/30 dark:bg-blue-950/40 dark:text-blue-200" animate={{ y: [0, -2, 0] }} transition={{ duration: 2, repeat: Infinity }}>
              4.9 stars from 1,248 verified users
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "video-widget") {
    return (
      <div className={shellClass}>
        <div className={panelClass}>
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <div className="rounded-xl border border-slate-200/35 bg-slate-50 p-2 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200">Video prompts</div>
            <div className="space-y-2">
              {["Demo video popup", "Feature walkthrough", "Testimonial reel"].map((row, i) => (
                <motion.div key={row} className="rounded-lg border border-slate-200/35 bg-white p-2 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/70 dark:text-slate-200" animate={{ x: [0, i % 2 ? 3 : -3, 0] }} transition={{ duration: 2.2 + i * 0.2, repeat: Infinity }}>
                  {row}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (normalizedPreset === "pricing") {
    const fallbackPlans: Array<Record<string, unknown>> = [
      { name: "Seed", monthly: 0, currency: "INR" },
      { name: "Sprout", monthly: 996, currency: "INR" },
      { name: "Treehouse", monthly: 1494, currency: "INR", isPopular: true },
      { name: "Rainforest", monthly: null, currency: "INR" },
    ];
    const displayPlans = (pricingHeroPlans.length ? pricingHeroPlans : fallbackPlans).slice(0, 4);
    const formatPlanPrice = (plan: Record<string, unknown>) => {
      const name = asString(plan.name).toLowerCase();
      if (name === "rainforest") return "Custom";
      const numeric = Number(plan.monthly ?? plan.priceMonthly ?? plan.price);
      if (!Number.isFinite(numeric)) return "Custom";
      const currency = asString(plan.currency, "INR").toUpperCase();
      const symbol = currency === "USD" ? "$" : "₹";
      return `${symbol}${Math.round(numeric).toLocaleString()}`;
    };

    return (
      <div className="relative w-full">
        <div className="grid gap-4 sm:grid-cols-4">
          {displayPlans.map((plan, i) => (
            <motion.div key={`${asString(plan.name)}-${i}`} className="rounded-2xl border border-white/10 bg-gradient-to-br from-white to-slate-50 p-4 text-slate-900 shadow-md dark:border-slate-700/35 dark:from-slate-900 dark:to-slate-800 dark:text-slate-100" whileHover={{ y: -4 }}>
              <div className="text-sm font-semibold">{asString(plan.name)}</div>
              <div className="mt-3 text-2xl font-bold">{formatPlanPrice(plan)}</div>
              <div className="mt-2 text-sm text-muted-foreground">{Boolean(plan.isPopular) ? "Most Popular" : "Plan"}</div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[36px] [transform-style:preserve-3d]" onMouseMove={handleTiltMove} onMouseLeave={resetTilt}>
      <div
        className="pointer-events-none absolute inset-0 rounded-[36px] bg-gradient-to-tr from-blue-500/30 via-violet-500/30 to-fuchsia-500/30 blur-2xl transition-transform duration-200 sm:-inset-4"
        style={{ transform: `translate3d(${tilt.y * 2}px, ${-tilt.x * 2}px, 0)` }}
      />
      <div
        className={`${shellClass} transition-transform duration-200 ease-out will-change-transform`}
        style={{ transform: `perspective(1200px) rotateX(${tilt.x.toFixed(2)}deg) rotateY(${tilt.y.toFixed(2)}deg)` }}
      >
        <div className={panelClass}>
          <div className="mb-4 flex items-center justify-between" style={{ transform: `translate3d(${tilt.y * 0.9}px, ${-tilt.x * 0.9}px, 28px)` }}>
            <div>
              <div className="text-sm font-bold text-slate-900 dark:text-white">NotifyStudio Command Center</div>
              <div className="mt-1 text-xs text-slate-500">Live engagement orchestration</div>
            </div>
            <div className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Live
            </div>
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]" style={{ transform: `translate3d(${tilt.y * 0.6}px, ${-tilt.x * 0.6}px, 20px)` }}>
            <div className="rounded-2xl border border-slate-100/35 bg-gradient-to-br from-slate-50 to-blue-50/60 p-4 dark:border-slate-700/35 dark:from-slate-800 dark:to-blue-950/30">
              <div className="relative mx-auto mt-2 h-44 w-44">
                <motion.div
                  className="absolute inset-0 rounded-full border border-slate-300/70 dark:border-slate-600/70"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
                >
                  {[
                    { label: "Popup Targeting", cls: "-top-3 left-1/2 -translate-x-1/2" },
                    { label: "Exit Intent", cls: "top-1/2 -right-4 -translate-y-1/2" },
                    { label: "Lead Capture", cls: "-bottom-3 left-1/2 -translate-x-1/2" },
                    { label: "Social Proof", cls: "top-1/2 -left-4 -translate-y-1/2" },
                  ].map((item) => (
                    <div key={item.label} className={`absolute ${item.cls} rounded-full border border-slate-200/70 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-700 shadow-sm dark:border-slate-700/70 dark:bg-slate-800 dark:text-slate-200`}>
                      {item.label}
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  className="absolute inset-5 rounded-full border border-cyan-200/70 bg-gradient-to-br from-cyan-100 to-violet-100 dark:border-cyan-700/50 dark:from-cyan-900/40 dark:to-violet-900/40"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
                />

                <div className="absolute inset-[38px] rounded-full border border-slate-200/70 bg-white/90 p-3 text-center shadow-md dark:border-slate-700/70 dark:bg-slate-900/80">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">NotifyStudio Core</div>
                  <div className="mt-2 text-2xl font-extrabold text-slate-900 dark:text-white">99.98%</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-emerald-600">Delivery uptime</div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2">
                  {[
                    { label: "Popup Views", value: "2.4M" },
                    { label: "Leads Captured", value: "890K" },
                    { label: "Conversion Lift", value: "41%" },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl border border-slate-200/60 bg-white/90 p-2 text-center dark:border-slate-700/60 dark:bg-slate-800/80">
                    <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">{item.label}</div>
                    <div className="mt-1 text-sm font-bold text-slate-900 dark:text-white">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
                {[
                  { title: "Popup Targeting", progress: 86 },
                  { title: "Behavior Triggers", progress: 72 },
                  { title: "Audience Segments", progress: 79 },
              ].map((item, idx) => (
                <div key={item.title} className="rounded-2xl border border-slate-100/35 bg-white p-3 dark:border-slate-700/35 dark:bg-slate-800/80">
                  <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{item.title}</div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 via-blue-600 to-violet-500"
                      animate={{ width: [`${Math.max(20, item.progress - 10)}%`, `${item.progress}%`, `${Math.max(20, item.progress - 6)}%`] }}
                      transition={{ duration: 2.2 + idx * 0.25, repeat: Infinity }}
                    />
                  </div>
                </div>
              ))}

              <motion.div
                className="rounded-2xl border border-slate-100/35 bg-white p-3 text-xs text-slate-700 dark:border-slate-700/35 dark:bg-slate-800/80 dark:text-slate-200"
                animate={{ y: [0, -2, 0] }}
                transition={{ duration: 2.2, repeat: Infinity }}
              >
                Real-time orchestration across popups, counters, social proof, and conversion automations.
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Hero ---------------- */

export function SaasHero({ content }: { content: Content }) {
  const metrics = asList(content.metrics);
  const filteredMetrics = metrics.filter((m) => {
    const label = asString(m.label).toLowerCase();
    const value = asString(m.value).toLowerCase();
    const bannedLabels = ["to start", "setup", "analytics", "growth tools", "unified hub", "credit card"];
    const bannedValues = ["free", "fast", "live", "10+", "1", "0"];
    if (bannedLabels.includes(label)) return false;
    if (bannedValues.includes(value)) return false;
    return true;
  });
  const tabs = asList(content.tabs);
  const [activeTab, setActiveTab] = useState(0);
  const pathname = usePathname();
  const isHome = pathname === "/";
  const gridColsClass = isHome ? "lg:grid-cols-[1fr_0.9fr]" : "lg:grid-cols-[1fr_1.05fr]";

  // read site-wide typography/typology settings (may include headingFont, headingStyle, headingGradientColors)
  const { settings } = useSiteSettings();
  const headingFont = asString(content.headingFont) || (settings as any)?.typography?.headingFont || undefined;
  const headingGradientColors = Array.isArray((content as any).headingGradientColors)
    ? ((content as any).headingGradientColors as string[])
    : (settings as any)?.typography?.headingGradientColors;
  const headingGradient = headingGradientColors ? `linear-gradient(120deg, ${headingGradientColors.join(", ")})` : undefined;
  const useRainbow = asString(content.headingStyle).toLowerCase() === "rainbow" || ((settings as any)?.typography?.headingStyle || "").toLowerCase() === "rainbow";

  // compute inline style for heading: include custom font and make homepage headings responsive to fit within two lines
  let headingInlineStyle: any = headingFont ? ({ fontFamily: headingFont } as any) : {};
  if (isHome) {
    // clamp size so rotating words fit within roughly two lines at large breakpoints
    headingInlineStyle.fontSize = "clamp(1.75rem, 4.2vw, 2.75rem)";
    headingInlineStyle.lineHeight = 1.02;
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-violet-50/60 py-14 lg:py-20 dark:from-[#070a18] dark:via-[#0a0f24] dark:to-[#0d0a1f]">
      {/* animated blobs */}
      <div className="blob h-[420px] w-[420px] bg-blue-400/40 -left-32 top-10" />
      <div className="blob h-[460px] w-[460px] bg-violet-400/40 right-[-120px] top-40" style={{ animationDelay: "-6s" }} />
      <div className="blob h-[320px] w-[320px] bg-cyan-400/30 left-1/3 bottom-[-80px]" style={{ animationDelay: "-12s" }} />

      <div className={`relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 ${gridColsClass} lg:px-8`}>
        <div>
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/70 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-white/5 dark:text-blue-200">
            <Sparkles className="h-4 w-4" />
            {asString(content.badge, "Website engagement platform for conversion teams")}
          </div>
          <h1 style={Object.keys(headingInlineStyle || {}).length ? headingInlineStyle : undefined} className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight text-slate-950 dark:text-white max-w-3xl">
            <AnimatedHeadline
              base={asString(content.heading, "Turn passive traffic into active conversions")}
              animatedWords={Array.isArray(content.animatedWords) ? (content.animatedWords as string[]) : undefined}
              animationType={asString(content.headingAnimation) || asString(content.animationType)}
              highlight={asString(content.headingHighlight) || asString(content.highlightText)}
              useRainbow={useRainbow}
              rainbowGradient={headingGradient}
            />
            {asString(content.headingAccent, "") ? (
              <span
                className={`block mt-1 ${useRainbow ? "rainbow-text" : "bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-500 bg-clip-text text-transparent"} text-2xl sm:text-3xl md:text-3xl lg:text-4xl xl:text-5xl`}
                style={headingGradient ? ({ ["--animated-spectrum" as any]: headingGradient } as any) : undefined}
              >
                {asString(content.headingAccent)}
              </span>
            ) : null}
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600 dark:text-slate-300 lg:text-xl">{asString(content.description)}</p>

          {tabs.length && !isHome ? (
            <div className="mt-8 flex max-w-xl flex-wrap gap-1 rounded-2xl border border-white/70 bg-white/80 p-1.5 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/5">
              {tabs.map((tab, index) => (
                <button
                  key={asString(tab.label) + index}
                  onClick={() => setActiveTab(index)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                    activeTab === index
                      ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md"
                      : "text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-white/10"
                  }`}
                >
                  <IconRenderer iconKey={asString(tab.iconKey, "Link2")} className="h-4 w-4" emojiClassName="text-base leading-none" />
                  {asString(tab.label)}
                </button>
              ))}
            </div>
          ) : null}

          <CtaButtons content={content} align="left" />

          {filteredMetrics.length ? (
            <div className="mt-10 grid max-w-xl grid-cols-3 gap-3">
              {filteredMetrics.map((metric, i) => (
                <Reveal key={asString(metric.label) + i} delay={i * 100}>
                  <div className="rounded-2xl border border-white/70 bg-white/75 p-4 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/5">
                    <div className="bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-2xl font-bold text-transparent">
                      <AnimatedNumber raw={asString(metric.value)} />
                    </div>
                    <div className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{asString(metric.label)}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : null}
        </div>

        <Reveal delay={150}>
          <div className="relative">
            <RightVisual content={content} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function NewsletterInline() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function subscribe(e?: React.FormEvent) {
    e?.preventDefault();
    setMessage(null);
    if (!email || !email.includes("@")) {
      setMessage('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captchaToken: '' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setMessage(body?.message || 'Subscription failed');
      } else {
        setMessage('Thanks — check your inbox for a confirmation.');
        setEmail('');
      }
    } catch (err) {
      setMessage('Network error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={subscribe} className="mt-4 flex max-w-md items-center gap-2">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        aria-label="Email address"
        className="w-full rounded-lg border border-border px-4 py-2 text-sm"
      />
      <Button type="submit" variant="gradient" size="sm" disabled={loading}>
        {loading ? 'Joining…' : 'Join Free'}
      </Button>
      {message ? <div className="mt-2 text-sm text-muted-foreground w-full">{message}</div> : null}
    </form>
  );
}

/* ---------------- FeatureStory ---------------- */

export function FeatureStory({ content }: { content: Content }) {
  const items = asList(content.items);
  const reverse = Boolean(content.reverse);

  return (
    <section className="relative overflow-x-clip py-14 lg:py-28">
      <div className={`mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[5fr_7fr] lg:px-8 [&>*]:min-w-0 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <Reveal className="min-w-0">
          <div className="min-w-0">
            <p className="inline-block rounded-full bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Feature")}
            </p>
            <h2 className="mt-3 break-words text-2xl font-bold tracking-tight leading-tight text-foreground sm:text-3xl md:text-3xl lg:text-4xl xl:text-5xl"><span className="rainbow-text">{asString(content.heading)}</span></h2>
            <p className="mt-5 break-words text-lg leading-8 text-muted-foreground">{asString(content.description)}</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {items.map((item, i) => {
                const cardHref = resolveFeatureDetailHref(item);
                return (
                  <Link
                    key={asString(item.title) + i}
                    href={cardHref}
                    className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 dark:hover:border-blue-700"
                  >
                    <IconBubble iconKey={asString(item.iconKey, "Sparkles")} tone={["default", "violet", "emerald", "amber"][i % 4] as "default"} />
                    <h3 className="mt-4 font-bold text-foreground">{asString(item.title)}</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{describeCard(item)}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary transition group-hover:gap-3">
                      {getCardCtaLabel(cardHref, asString(item.title || item.name))}
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                );
              })}
            </div>
            <CtaButtons content={content} align="left" />
          </div>
        </Reveal>
        <Reveal delay={150} className="min-w-0">
          <div className="relative overflow-hidden rounded-[36px]">
            <div className="pointer-events-none absolute inset-0 rounded-[36px] bg-gradient-to-tr from-blue-400/20 via-violet-400/20 to-fuchsia-400/20 blur-2xl sm:-inset-4 sm:blur-3xl" />
            <div className="gradient-border relative rounded-[32px] bg-gradient-to-br from-white to-slate-50 p-4 shadow-2xl shadow-violet-900/10 dark:from-slate-950 dark:to-slate-900">
              <div className="rounded-[24px] bg-slate-950 p-5 text-white">
                <div className="mb-5 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold">{asString(content.previewTitle, "Campaign workspace")}</div>
                    <div className="mt-1 text-xs text-slate-400">{asString(content.previewSubtitle, "Live performance")}</div>
                  </div>
                  <div className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-300">Optimized</div>
                </div>
                <div className="space-y-3">
                  {["notifystudio.com/popup/launch-offer", "notifystudio.com/flow/lead-magnet", "notifystudio.com/campaign/countdown"].map((link, index) => (
                    <div key={link} className="flex items-center justify-between rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 transition hover:bg-white/10">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{link}</div>
                        <div className="mt-1 text-xs text-slate-400">{[8421, 3910, 1276][index].toLocaleString()} clicks · 30d</div>
                      </div>
                      <Copy className="h-4 w-4 text-slate-400" />
                    </div>
                  ))}
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3">
                  {[
                    ["CTR", "31%", "from-blue-500 to-violet-500"],
                    ["Scans", "12.8k", "from-emerald-500 to-cyan-500"],
                    ["Leads", "846", "from-amber-500 to-rose-500"],
                  ].map(([label, value, grad]) => (
                    <div key={label} className="rounded-2xl bg-white p-4 text-slate-950">
                      <div className="text-xs text-slate-500">{label}</div>
                      <div className={`mt-1 bg-gradient-to-r ${grad} bg-clip-text text-xl font-bold text-transparent`}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- ImpactStats ---------------- */

export function ImpactStats({ content }: { content: Content }) {
  const stats = asList(content.items);

  return (
    <section className="relative overflow-hidden bg-slate-950 py-12 text-white lg:py-16">
      <div className="blob h-[400px] w-[400px] bg-violet-600/40 left-[-100px] top-10" />
      <div className="blob h-[400px] w-[400px] bg-blue-600/40 right-[-100px] bottom-10" style={{ animationDelay: "-8s" }} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <Reveal>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-300">{asString(content.eyebrow, "Proven impact")}</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight lg:text-5xl"><span className="rainbow-text">{asString(content.heading)}</span></h2>
              <p className="mt-4 text-lg leading-8 text-slate-300">{asString(content.description)}</p>
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => (
              <Reveal key={asString(stat.label) + i} delay={i * 80}>
                <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition hover:border-white/30 hover:bg-white/10">
                  <div className="absolute inset-x-0 -top-1 h-px bg-gradient-to-r from-transparent via-violet-400 to-transparent opacity-60" />
                  <div className="bg-gradient-to-br from-white via-blue-200 to-violet-200 bg-clip-text text-4xl font-bold text-transparent">
                    <AnimatedNumber raw={asString(stat.value)} />
                  </div>
                  <div className="mt-2 text-sm text-slate-300">{asString(stat.label)}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- AgencyScale (bento) ---------------- */

export function AgencyScale({ content }: { content: Content }) {
  const items = asList(content.items);

  return (
    <section className="bg-gradient-to-b from-muted/30 via-background to-muted/30 py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "For agencies")}
            </p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight leading-tight text-foreground sm:text-3xl md:text-3xl lg:text-4xl xl:text-5xl">{asString(content.heading)}</h2>
            <p className="mt-4 text-lg leading-8 text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>
        <div className="grid auto-rows-[minmax(180px,auto)] grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-6">
          {items.map((item, i) => {
            // bento span pattern
            const spans = [
              "md:col-span-2 lg:col-span-3 lg:row-span-2",
              "md:col-span-1 lg:col-span-3",
              "md:col-span-1 lg:col-span-2",
              "md:col-span-1 lg:col-span-2",
              "md:col-span-1 lg:col-span-2",
              "md:col-span-3 lg:col-span-3",
            ];
            const tones = ["default", "violet", "emerald", "amber"] as const;
            const span = spans[i % spans.length];
            const resolvedHref = resolveFeatureDetailHref(item);
            const clickable = Boolean(resolvedHref);
            return (
              <Reveal key={asString(item.title) + i} delay={i * 80} className={span}>
                {clickable ? (
                  <Link href={resolvedHref} className="group block h-full">
                    <div className="relative h-full overflow-hidden rounded-3xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-500/10">
                      <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-blue-500/10 to-violet-500/10 blur-2xl transition group-hover:from-blue-500/30 group-hover:to-violet-500/30" />
                      <IconBubble iconKey={asString(item.iconKey, "Users")} tone={tones[i % tones.length]} />
                      <h3 className="relative mt-5 text-xl font-bold text-foreground">{asString(item.title)}</h3>
                      <p className="relative mt-3 text-sm leading-7 text-muted-foreground">{describeCard(item)}</p>
                      <span className="relative mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary transition group-hover:gap-3">
                        {getCardCtaLabel(resolvedHref, asString(item.title || item.name))}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                ) : (
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-500/10">
                    <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-blue-500/10 to-violet-500/10 blur-2xl transition group-hover:from-blue-500/30 group-hover:to-violet-500/30" />
                    <IconBubble iconKey={asString(item.iconKey, "Users")} tone={tones[i % tones.length]} />
                    <h3 className="relative mt-5 text-xl font-bold text-foreground">{asString(item.title)}</h3>
                    <p className="relative mt-3 text-sm leading-7 text-muted-foreground">{describeCard(item)}</p>
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Testimonial Carousel (shared) ---------------- */

function TestimonialCarousel({ items }: { items: Array<Record<string, unknown>> }) {
  if (!items.length) return null;

  const [cur, setCur] = useState(0);
  const [perView, setPerView] = useState(3);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const hovering = useRef(false);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const maxCur = Math.max(0, items.length - perView);

  useEffect(() => {
    const id = setInterval(() => {
      if (hovering.current) return;
      setCur((c) => (c >= maxCur ? 0 : c + 1));
    }, 4500);
    return () => clearInterval(id);
  }, [maxCur]);

  useEffect(() => {
    if (cur > maxCur) setCur(maxCur);
  }, [cur, maxCur]);

  const go = (delta: number) => setCur((c) => Math.min(maxCur, Math.max(0, c + delta)));
  const dots = Math.max(1, items.length - perView + 1);
  const widthPct = 100 / perView;

  return (
    <div
      className="relative"
      onMouseEnter={() => (hovering.current = true)}
      onMouseLeave={() => (hovering.current = false)}
    >
      <div className="overflow-hidden">
        <div
          ref={trackRef}
          className="flex gap-5 transition-transform duration-700 ease-out"
          style={{ transform: `translateX(calc(-${cur * widthPct}% - ${cur * 20}px))` }}
        >
          {items.map((item, i) => (
            <div key={asString(item.name) + i} style={{ flex: `0 0 calc(${widthPct}% - ${(20 * (perView - 1)) / perView}px)` }}>
              <Card className="h-full border-border/80 bg-card transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-500/10">
                <CardContent className="flex h-full flex-col p-7">
                  <div className="mb-4 flex gap-1 text-amber-400">
                    {Array.from({ length: 5 }).map((_, idx) => <Star key={idx} className="h-4 w-4 fill-current" />)}
                  </div>
                  <p className="flex-1 text-base leading-7 text-foreground">&ldquo;{asString(item.content || item.feedback)}&rdquo;</p>
                  <div className="mt-6 flex items-center gap-3 border-t border-border/60 pt-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-sm font-bold text-white">
                      {asString(item.name).slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-bold text-foreground">{asString(item.name)}</p>
                      <p className="text-sm text-muted-foreground">{asString(item.role || item.company)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 flex items-center justify-center gap-3">
        <button
          onClick={() => go(-1)}
          aria-label="Previous"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground transition hover:bg-muted disabled:opacity-40"
          disabled={cur === 0}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex gap-2">
          {Array.from({ length: dots }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCur(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`h-2 rounded-full transition-all ${idx === cur ? "w-8 bg-gradient-to-r from-blue-600 to-violet-600" : "w-2 bg-border hover:bg-muted-foreground/40"}`}
            />
          ))}
        </div>
        <button
          onClick={() => go(1)}
          aria-label="Next"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground transition hover:bg-muted disabled:opacity-40"
          disabled={cur >= maxCur}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

/* ---------------- ReviewWall ---------------- */

export function ReviewWall({ content }: { content: Content }) {
  const ratings = asList(content.ratings);
  const items = asList(content.items);

  if (!items.length) return null;

  return (
    <section className="py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <Reveal>
            <div className="w-full">
              <h2 className="max-w-5xl text-balance text-3xl font-bold tracking-tight text-foreground lg:text-5xl">{asString(content.heading)}</h2>
              <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{asString(content.description)}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {ratings.map((rating) => (
                <div key={asString(rating.name)} className="gradient-border rounded-2xl bg-card p-4 text-center">
                  <div className="bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-2xl font-bold text-transparent">
                    <AnimatedNumber raw={asString(rating.value)} />
                  </div>
                  <div className="mt-1 text-xs font-semibold text-muted-foreground">{asString(rating.name)}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <TestimonialCarousel items={items} />
      </div>
    </section>
  );
}

/* ---------------- LogoCarousel (dual marquee with fade) ---------------- */

export function LogoCarousel({ content }: { content: Content }) {
  const logos = asList(content.items);
  if (!logos.length) return null;
  const items = [...logos, ...logos];

  return (
    <section className="overflow-x-clip border-y border-border bg-background py-12">
      <div className="mx-auto max-w-7xl overflow-x-clip px-4 sm:px-6 lg:px-8">
        <p className="mb-6 text-center text-sm font-bold uppercase tracking-[0.22em] text-muted-foreground">
          {asString(content.heading, "Trusted by teams growing with smarter links")}
        </p>
        <div className="marquee-fade relative w-full space-y-4 overflow-x-clip [contain:paint]">
          <div className="flex w-max gap-4 [animation:clientMarquee_36s_linear_infinite] hover:[animation-play-state:paused]">
            {items.map((item, index) => (
              <div
                key={`${asString(item.name)}-${index}`}
                className="flex min-w-[170px] items-center justify-center rounded-2xl border border-border bg-card px-5 py-4 text-sm font-bold text-muted-foreground transition hover:border-blue-300 hover:text-foreground"
              >
                {asString(item.name)}
              </div>
            ))}
          </div>
          {logos.length > 4 ? (
            <div className="flex w-max gap-4 [animation:marquee-reverse_44s_linear_infinite] hover:[animation-play-state:paused]">
              {items.slice().reverse().map((item, index) => (
                <div
                  key={`r-${asString(item.name)}-${index}`}
                  className="flex min-w-[170px] items-center justify-center rounded-2xl border border-border bg-card px-5 py-4 text-sm font-bold text-muted-foreground transition hover:border-violet-300 hover:text-foreground"
                >
                  {asString(item.name)}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SaasGrid ---------------- */

export function SaasGrid({ content, columns = "lg:grid-cols-3" }: { content: Content; columns?: string }) {
  const items = asList(content.items || content.features);
  const tones = ["default", "violet", "emerald", "amber"] as const;
  const shouldAnimateCards = items.length <= 8;

  return (
    <section className="py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-14 max-w-3xl text-center">
            {asString(content.eyebrow) ? (
              <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
                {asString(content.eyebrow)}
              </p>
            ) : null}
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground lg:text-5xl">{asString(content.heading)}</h2>
            {asString(content.description) ? <p className="mt-4 text-lg leading-8 text-muted-foreground">{asString(content.description)}</p> : null}
          </div>
        </Reveal>
        <div className={`grid gap-5 md:grid-cols-2 ${columns}`} style={{ contentVisibility: "auto", containIntrinsicSize: "1200px" }}>
          {items.map((item, i) => {
            const cardHref = resolveFeatureDetailHref(item);
            return (
              <Reveal key={asString(item.title || item.name) + i} delay={shouldAnimateCards ? (i % 4) * 80 : 0} className={shouldAnimateCards ? "" : "in"}>
                <Card className="group relative h-full overflow-hidden border-border/80 bg-card transition-colors duration-300 hover:border-blue-300 dark:hover:border-blue-700">
                  <CardContent className="relative p-6">
                    <IconBubble iconKey={asString(item.iconKey, "Sparkles")} tone={tones[i % tones.length]} />
                    <h3 className="mt-5 text-xl font-bold text-foreground">{asString(item.title || item.name)}</h3>
                    <p className="mt-3 text-sm leading-7 text-muted-foreground">{describeCard(item)}</p>
                    <Link
                      href={cardHref}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-primary transition group-hover:gap-3"
                    >
                      {getCardCtaLabel(cardHref, asString(item.title || item.name))} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- ProductDemo (tabbed) ---------------- */

export function ProductDemo({ content }: { content: Content }) {
  const items = asList(content.items);
  const [active, setActive] = useState(0);
  const current = items[active] || items[0];

  return (
    <section className="relative overflow-hidden bg-slate-950 py-12 text-white lg:py-16">
      <div className="blob h-[400px] w-[400px] bg-blue-600/40 left-1/4 top-0" />
      <div className="blob h-[400px] w-[400px] bg-fuchsia-600/30 right-0 bottom-0" style={{ animationDelay: "-9s" }} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-300">{asString(content.eyebrow, "Product demo")}</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight lg:text-5xl">{asString(content.heading)}</h2>
            <p className="mt-4 text-lg leading-8 text-slate-300">{asString(content.description)}</p>
          </div>
        </Reveal>

        {items.length ? (
          <div className="mx-auto mb-10 flex max-w-2xl flex-wrap justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-1.5 backdrop-blur">
            {items.map((item, i) => (
              <button
                key={asString(item.title) + i}
                onClick={() => setActive(i)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
                  active === i
                    ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md"
                    : "text-slate-300 hover:bg-white/10"
                }`}
              >
                <IconRenderer iconKey={asString(item.iconKey, "Zap")} className="h-4 w-4" emojiClassName="text-base leading-none" />
                {asString(item.title)}
              </button>
            ))}
          </div>
        ) : null}

        {current ? (
          <Reveal>
            <div className="mx-auto max-w-5xl">
              <div className="gradient-border rounded-3xl bg-white/5 p-6 backdrop-blur lg:p-10">
                <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
                  <div>
                    <IconBubble iconKey={asString(current.iconKey, "Zap")} tone="violet" />
                    <h3 className="mt-5 text-2xl font-bold lg:text-3xl">{asString(current.title)}</h3>
                    <p className="mt-3 leading-7 text-slate-300">{asString(current.description)}</p>
                    <CtaButtons content={content} align="left" />
                  </div>
                  <div className="rounded-2xl bg-slate-900 p-6 shadow-2xl ring-1 ring-white/10">
                    <div className="mb-4 flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-rose-500" />
                      <span className="h-3 w-3 rounded-full bg-amber-400" />
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                    </div>
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((row) => (
                        <div key={row} className="flex items-center justify-between rounded-xl bg-white/5 p-3">
                          <div className="h-2 w-32 rounded-full bg-white/20" />
                          <div className="h-2 w-12 rounded-full bg-gradient-to-r from-blue-500 to-violet-500" />
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex h-24 items-end gap-1.5">
                      {[40, 65, 50, 80, 70, 95, 85].map((h, idx) => (
                        <div
                          key={idx}
                          className="shimmer-bar flex-1 rounded-t-lg bg-gradient-to-t from-blue-600 via-violet-500 to-fuchsia-400"
                          style={{ height: `${h}%`, animationDelay: `${idx * 120}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ) : null}
      </div>
    </section>
  );
}

/* ---------------- PricingPreview ---------------- */

export function PricingPreview({ content }: { content: Content }) {
  const cmsPlans = asList(content.plans || content.items);
  const [annual, setAnnual] = useState(false);
  const currencyOptions = Array.from(new Set((asList(content.currencies).map((c) => asString(c)).filter(Boolean).length
    ? asList(content.currencies).map((c) => asString(c)).filter(Boolean)
    : ["INR", "USD"]).map((c) => c.toUpperCase())));
  const [selectedCurrency, setSelectedCurrency] = useState(currencyOptions[0] || "INR");
  const [apiPlans, setApiPlans] = useState<Content[]>([]);
  const [apiFeatureRows, setApiFeatureRows] = useState<Array<{ name: string; values: Array<string | boolean | null> }>>([]);

  useEffect(() => {
    let mounted = true;
    getPublicPricing({ currency: selectedCurrency, billing: annual ? "yearly" : "monthly" })
      .then((res) => {
        if (!mounted) return;
        setApiPlans(Array.isArray(res.plans) ? (res.plans as unknown as Content[]) : []);
        setApiFeatureRows(Array.isArray(res.features) ? (res.features as Array<{ name: string; values: Array<string | boolean | null> }>) : []);
      })
      .catch(() => {
        if (!mounted) return;
        setApiPlans([]);
        setApiFeatureRows([]);
      });

    return () => {
      mounted = false;
    };
  }, [annual, selectedCurrency]);

  const plans = cmsPlans.map((plan, idx) => {
    const planName = asString(plan.name).toLowerCase();
    const byName = apiPlans.find((p) => asString(p.name).toLowerCase() === planName);
    const bySlug = apiPlans.find((p) => asString(p.slug).toLowerCase() === asString(plan.slug).toLowerCase());
    const fromDb = bySlug || byName || apiPlans[idx];
    if (!fromDb) return plan;

    return {
      ...plan,
      name: asString(fromDb.name, asString(plan.name)),
      slug: asString(fromDb.slug, asString(plan.slug)),
      monthlyPrice: fromDb.priceMonthly ?? fromDb.monthly ?? plan.monthlyPrice ?? plan.priceMonthly ?? plan.price ?? plan.monthly,
      annualPrice: fromDb.priceYearly ?? fromDb.annual ?? plan.annualPrice ?? plan.priceYearly ?? plan.yearly ?? plan.annual,
      ctaText: asString(fromDb.ctaText, asString(plan.ctaText)),
      ctaUrl: asString(fromDb.ctaUrl, asString(plan.ctaUrl)),
      isPopular: typeof fromDb.isPopular === "boolean" ? fromDb.isPopular : plan.isPopular,
    };
  });

  const plansWithFeatures: Content[] = plans.map((plan, idx) => {
    const normalizedFeatureItems = apiFeatureRows
      .map((featureRow) => {
        const rawValue = featureRow.values?.[idx];
        if (rawValue === false || rawValue == null || rawValue === "") return null;

        if (rawValue === true) {
          return { text: featureRow.name };
        }

        const valueText = asString(rawValue).trim();
        if (!valueText) return null;

        if (/^(yes|true|all)$/i.test(valueText)) {
          return { text: featureRow.name };
        }

        return { text: `${featureRow.name}: ${valueText}` };
      })
      .filter(Boolean) as Array<{ text: string }>;

    const existing = asList(plan.features)
      .map((feature) => {
        const text = asString((feature as Record<string, unknown>)?.text || feature).trim();
        return text ? { text } : null;
      })
      .filter(Boolean) as Array<{ text: string }>;

    return {
      ...plan,
      features: normalizedFeatureItems.length ? normalizedFeatureItems : existing,
    };
  });

  function asCurrencyPrice(value: unknown, currency: string) {
    if (typeof value === "number" && Number.isFinite(value)) {
      const symbol = currency === "INR" ? "₹" : "$";
      return `${symbol}${Math.round(value).toLocaleString()}`;
    }
    return asString(value);
  }

  function convertPrice(raw: string, targetCurrency: string) {
    const numeric = Number(raw.replace(/[^\d.]/g, ""));
    if (!Number.isFinite(numeric) || numeric < 0) return raw;

    const hasRupee = /₹|\bINR\b/i.test(raw);
    const hasDollar = /\$|\bUSD\b/i.test(raw);
    const from = hasRupee ? "INR" : hasDollar ? "USD" : targetCurrency;
    const rate = 83;
    const converted = from === targetCurrency
      ? numeric
      : from === "USD" && targetCurrency === "INR"
      ? numeric * rate
      : numeric / rate;
    const rounded = Math.round(converted);
    const symbol = targetCurrency === "INR" ? "₹" : "$";
    return `${symbol}${rounded.toLocaleString()}`;
  }

  return (
    <section className="bg-gradient-to-b from-muted/30 via-background to-muted/30 py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Pricing")}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground lg:text-5xl">
              {asString(content.heading, "Simple plans for every growth stage")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>

        <div className="mx-auto mb-12 flex w-fit items-center gap-1 rounded-full border border-border bg-card p-1.5 shadow-sm">
          <button
            onClick={() => setAnnual(false)}
            className={`rounded-full px-5 py-2 text-sm font-bold transition ${!annual ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md" : "text-muted-foreground"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition ${annual ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md" : "text-muted-foreground"}`}
          >
            Annual
            <span className={`rounded-full px-2 py-0.5 text-[10px] ${annual ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"}`}>
              Save 20%
            </span>
          </button>
        </div>

        <div className="mx-auto -mt-6 mb-10 flex w-fit items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold text-muted-foreground shadow-sm">
          <span>Currency</span>
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold text-foreground"
            aria-label="Select pricing currency"
          >
            {currencyOptions.map((currency) => (
              <option key={currency} value={currency}>{currency}</option>
            ))}
          </select>
        </div>

        <div className="grid gap-6 lg:grid-cols-4">
          {plansWithFeatures.map((plan, i) => {
            const popular = Boolean(plan.isPopular);
            const isCustomRainforest = asString(plan.name).toLowerCase() === "rainforest";
            const monthlyRaw =
              asCurrencyPrice(plan.monthlyPrice, selectedCurrency) ||
              asCurrencyPrice(plan.priceMonthly, selectedCurrency) ||
              asCurrencyPrice(plan.price, selectedCurrency) ||
              asCurrencyPrice(plan.monthly, selectedCurrency);
            const yearlyRaw =
              asCurrencyPrice(plan.annualPrice, selectedCurrency) ||
              asCurrencyPrice(plan.priceYearly, selectedCurrency) ||
              asCurrencyPrice(plan.yearly, selectedCurrency) ||
              asCurrencyPrice(plan.annual, selectedCurrency);
            const discountPercent = Number(plan.discountPercent ?? content.discountPercent ?? 20);

            const monthlyNumeric = Number(monthlyRaw.replace(/[^\d.]/g, ""));
            const computedYearly = Number.isFinite(monthlyNumeric) && monthlyNumeric > 0
              ? `${selectedCurrency === "INR" ? "₹" : "$"}${Math.round(monthlyNumeric * 12 * (1 - Math.max(0, Math.min(100, discountPercent)) / 100)).toLocaleString()}`
              : "";

            const displayedBase = annual ? (yearlyRaw || computedYearly || monthlyRaw) : monthlyRaw;
            const displayed = isCustomRainforest ? "Custom pricing" : convertPrice(displayedBase, selectedCurrency);
            const ctaHref = isCustomRainforest
              ? resolvePublicPageUrl(asString(content.customPlanUrl, "/contact-us"))
              : resolvePublicPageUrl(asString(plan.ctaUrl, SIGNUP_DASHBOARD_URL));
            const ctaText = isCustomRainforest
              ? asString(content.customPlanCtaText, "Request Custom Plan")
              : asString(plan.ctaText, "Start Free Trial");
            return (
              <Reveal key={asString(plan.name) + i} delay={i * 100}>
                <Card
                  className={`relative h-full overflow-hidden transition ${
                    popular
                      ? "gradient-border border-transparent shadow-2xl shadow-violet-500/20 lg:-translate-y-3"
                      : "border-border hover:-translate-y-1 hover:shadow-xl"
                  }`}
                >
                  {popular ? (
                    <div className="absolute right-5 top-5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-3 py-1 text-xs font-bold text-white shadow-md">
                      Most Popular
                    </div>
                  ) : null}
                  <CardContent className="p-7">
                    <h3 className="text-2xl font-semibold text-foreground">{asString(plan.name)}</h3>
                    <p className="mt-2 min-h-12 text-sm leading-6 text-muted-foreground">{asString(plan.description)}</p>
                    <div className="mt-6 flex items-end gap-1">
                      <span
                        className={`text-5xl font-bold ${
                          popular ? "bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-transparent" : "text-foreground"
                        }`}
                      >
                        {displayed}
                      </span>
                      {!isCustomRainforest ? (
                        <span className="mb-2 text-sm text-muted-foreground">{asString(plan.period, annual ? "/yr" : "/mo")}</span>
                      ) : null}
                    </div>
                    {annual && !isCustomRainforest && (yearlyRaw || computedYearly) ? (
                      <p className="mt-1 text-xs font-semibold text-emerald-600">Billed annually · 2 months free</p>
                    ) : null}
                    <Link href={ctaHref}>
                      <Button className={`mt-6 w-full ${popular ? "glow-md" : ""}`} variant={popular ? "gradient" : "outline"}>
                        {ctaText}
                      </Button>
                    </Link>
                    <div className="mt-7 space-y-3 border-t border-border/60 pt-6">
                      {asList(plan.features).map((feature) => (
                        <div key={asString(feature.text)} className="flex gap-3 text-sm text-foreground/80">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          <span>{asString(feature.text)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-border/70 bg-card/80 p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Need a custom plan?</p>
              <p className="mt-1 text-base text-foreground">Enterprise volume, dedicated support, and bespoke onboarding for your team.</p>
            </div>
            <Link href={resolvePublicPageUrl(asString(content.customPlanUrl, "/contact"))}>
              <Button variant="outline" className="w-full sm:w-auto">
                {asString(content.customPlanCtaText, "Contact Us")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- TestimonialCards (carousel) ---------------- */

export function TestimonialCards({ content }: { content: Content }) {
  const items = asList(content.items);
  if (!items.length) return null;

  return (
    <section className="py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mb-14 w-full">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Testimonials")}
            </p>
            <h2 className="mt-3 max-w-5xl text-balance text-3xl font-bold tracking-tight text-foreground lg:text-5xl">
              {asString(content.heading, "Loved by modern marketing teams")}
            </h2>
            <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>
        <TestimonialCarousel items={items} />
      </div>
    </section>
  );
}

/* ---------------- SaasCta ---------------- */

export function SaasCta({ content }: { content: Content }) {
  const theme = resolveCtaTheme(content);
  return (
    <section className="py-14 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className={`relative overflow-hidden rounded-[32px] bg-gradient-to-br ${theme.sectionGradient} px-6 py-14 text-center text-white shadow-2xl ${theme.shadow} sm:px-10`}>
            <div className="blob h-[400px] w-[400px] bg-white/20 left-[-100px] top-[-100px]" />
            <div className="blob h-[400px] w-[400px] bg-white/10 right-[-100px] bottom-[-100px]" style={{ animationDelay: "-7s" }} />
            <div className="relative">
              <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" />
                {asString(content.eyebrow, "Get started today")}
              </div>
              <h2 className="mx-auto max-w-3xl text-3xl font-bold tracking-tight lg:text-5xl">{asString(content.heading)}</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-white/90">{asString(content.description)}</p>
              <CtaButtons content={content} />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- PricingComparisonTable ---------------- */

type CompPlan = { name: string; highlight?: boolean; ctaText?: string; ctaUrl?: string };
type CompCell = boolean | string | number | null | undefined;
type CompFeature = { name: string; tooltip?: string; values: CompCell[] };
type CompGroup = { name: string; features: CompFeature[] };

function renderCell(cell: CompCell) {
  if (cell === true || cell === "true" || cell === "yes") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    );
  }
  if (cell === false || cell === "false" || cell === "no" || cell == null || cell === "") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600">
        <Minus className="h-4 w-4" />
      </span>
    );
  }
  return <span className="text-sm font-bold text-foreground">{String(cell)}</span>;
}

export function PricingComparisonTable({ content }: { content: Content }) {
  const plans = asList<CompPlan>(content.plans);
  const groups = asList<{ name: string; features?: unknown }>(content.groups).map((g) => ({
    name: asString(g.name),
    features: asList<{ name?: unknown; tooltip?: unknown; values?: unknown }>(g.features).map((f) => ({
      name: asString(f.name),
      tooltip: asString(f.tooltip),
      values: Array.isArray(f.values) ? (f.values as CompCell[]) : [],
    })),
  })) as CompGroup[];

  const [activeMobilePlan, setActiveMobilePlan] = useState(0);

  if (!plans.length || !groups.length) return null;
  return (
    <section id="pricing-comparison-section" className="py-14 lg:py-28">
      <StickyPricingHeader plans={plans} sectionId="pricing-comparison-section" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-12 max-w-3xl text-center pricing-comparison-header">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Compare plans")}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground lg:text-5xl">
              {asString(content.heading, "Compare every feature, side by side")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>

        {/* Mobile plan switcher */}
        <div className="mb-6 flex flex-wrap justify-center gap-1 rounded-full border border-border bg-card p-1 lg:hidden">
          {plans.map((plan, i) => (
            <button
              key={plan.name + i}
              onClick={() => setActiveMobilePlan(i)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
                activeMobilePlan === i
                  ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow"
                  : "text-muted-foreground"
              }`}
            >
              {plan.name}
            </button>
          ))}
        </div>

        <Reveal>
          <div className="overflow-x-auto rounded-3xl border border-border bg-card shadow-xl shadow-violet-500/5">
            {/* Header (sticky on scroll) */}
            <div className="sticky top-16 z-20 grid grid-cols-[1.5fr_repeat(var(--cols,3),1fr)] gap-2 border-b border-border bg-card/95 px-4 py-5 backdrop-blur-md lg:grid-cols-[2fr_repeat(var(--cols,3),1fr)] lg:px-8" style={{ ["--cols" as any]: String(plans.length) }}>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground lg:sticky lg:left-0 lg:z-30 lg:bg-card">Features</div>
              {plans.map((plan, i) => (
                <div
                  key={plan.name + i}
                  className={`text-center transition ${
                    i === activeMobilePlan ? "" : "hidden lg:block"
                  }`}
                >
                  <div className={`text-base font-semibold ${plan.highlight ? "bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-transparent" : "text-foreground"}`}>
                    {plan.name}
                  </div>
                  {plan.highlight ? (
                    <div className="mx-auto mt-1 inline-block rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      Most Popular
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            {/* Groups */}
            {groups.map((group, gi) => (
              <div key={group.name + gi}>
                <div className="border-b border-border bg-gradient-to-r from-slate-50 to-blue-50/40 px-4 py-3 text-sm font-bold uppercase tracking-wider text-foreground dark:from-slate-900 dark:to-slate-900/40 lg:px-8">
                  {group.name}
                </div>
                {group.features.map((feature, fi) => (
                  <div
                    key={feature.name + fi}
                    className="grid grid-cols-[1.5fr_repeat(var(--cols,3),1fr)] gap-2 border-b border-border/60 px-4 py-4 transition hover:bg-muted/30 lg:grid-cols-[2fr_repeat(var(--cols,3),1fr)] lg:px-8"
                    style={{ ["--cols" as any]: String(plans.length), minWidth: `${280 + plans.length * 180}px` }}
                  >
                    <div className="text-sm font-medium text-foreground lg:sticky lg:left-0 lg:bg-card lg:z-10 lg:top-20">{feature.name}</div>
                    {plans.map((_, pi) => (
                      <div
                        key={pi}
                        className={`flex items-center justify-center transition ${
                          pi === activeMobilePlan ? "" : "hidden lg:flex"
                        }`}
                      >
                        {renderCell(feature.values[pi])}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}

            {/* CTA row */}
            <div className="grid grid-cols-[1.5fr_repeat(var(--cols,3),1fr)] gap-2 bg-gradient-to-r from-slate-50 to-blue-50/40 px-4 py-6 dark:from-slate-900 dark:to-slate-900/40 lg:grid-cols-[2fr_repeat(var(--cols,3),1fr)] lg:px-8" style={{ ["--cols" as any]: String(plans.length) }}>
              <div className="self-center text-sm font-bold text-foreground">Get started</div>
              {plans.map((plan, i) => (
                <div
                  key={plan.name + i}
                  className={`${i === activeMobilePlan ? "" : "hidden lg:block"}`}
                >
                  <Link href={resolvePublicPageUrl(asString(plan.ctaUrl, SIGNUP_DASHBOARD_URL))} className="block">
                    <Button
                      size="sm"
                      variant={plan.highlight ? "gradient" : "outline"}
                      className={`w-full ${plan.highlight ? "glow-md" : ""}`}
                    >
                      {asString(plan.ctaText, "Choose")}
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- AddOns grid ---------------- */

export function PricingAddons({ content }: { content: Content }) {
  const items = asList(content.items);
  if (!items.length) return null;
  return (
    <section className="bg-muted/30 py-14 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Add-ons")}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
              {asString(content.heading, "Power up any plan")}
            </h2>
            <p className="mt-3 text-lg text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={asString(item.title) + i} delay={i * 80}>
              <div className="group relative h-full overflow-hidden rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10">
                <div className="flex items-start justify-between gap-3">
                  <IconBubble iconKey={asString(item.iconKey, "Sparkles")} tone={(["default", "violet", "emerald", "amber"] as const)[i % 4]} />
                  {asString(item.price) ? (
                    <div className="text-right">
                      <div className="bg-gradient-to-br from-blue-600 to-violet-600 bg-clip-text text-2xl font-bold text-transparent">
                        {asString(item.price)}
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {asString(item.period, "/mo")}
                      </div>
                    </div>
                  ) : null}
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">{asString(item.title)}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{asString(item.description)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function comparisonCell(value: unknown, highlighted = false) {
  const text = asString(value);
  const normalized = text.toLowerCase();
  const positive = ["yes", "true", "included", "unlimited", "advanced", "full", "free"].some((token) => normalized.includes(token));
  const negative = ["no", "false", "limited", "paid only", "basic", "not included"].some((token) => normalized.includes(token));

  return (
    <div className={`flex items-center gap-2 rounded-2xl px-3 py-2 text-sm ${highlighted ? "bg-blue-50 font-semibold text-blue-800 dark:bg-blue-950/30 dark:text-blue-200" : "text-muted-foreground"}`}>
      {positive && !negative ? <Check className="h-4 w-4 shrink-0 text-emerald-500" /> : null}
      {negative ? <Minus className="h-4 w-4 shrink-0 text-slate-400" /> : null}
      <span>{text}</span>
    </div>
  );
}

export function ComparisonTableSection({ content }: { content: Content }) {
  const rows = asList(content.rows);
  const competitorName = asString(content.competitorName, "Competitor");

  if (!rows.length) return null;

  return (
    <section className="py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Quick comparison")}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground lg:text-5xl">{asString(content.heading)}</h2>
            <p className="mt-4 text-lg text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl shadow-violet-500/5">
            <div className="grid grid-cols-[1.05fr_1fr_1fr] gap-3 border-b border-border bg-muted/40 px-4 py-4 text-sm font-bold text-foreground md:px-6">
              <div>Feature</div>
              <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-violet-600 px-3 py-2 text-center text-white">NotifyStudio</div>
              <div className="px-3 py-2 text-center">{competitorName}</div>
            </div>
            {rows.map((row, index) => (
              <div key={`${asString(row.feature)}-${index}`} className={`grid grid-cols-[1.05fr_1fr_1fr] gap-3 px-4 py-4 md:px-6 ${index !== rows.length - 1 ? "border-b border-border/70" : ""} ${Boolean(row.highlight) ? "bg-blue-50/45 dark:bg-blue-950/10" : ""}`}>
                <div>
                  <div className="text-sm font-bold text-foreground">{asString(row.feature)}</div>
                  {asString(row.category) ? <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{asString(row.category)}</div> : null}
                </div>
                {comparisonCell((row.notifyStudioValue as string) || row.bjlistudioValue, true)}
                {comparisonCell(row.competitorValue)}
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function ComparisonBreakdown({ content }: { content: Content }) {
  const items = asList(content.items);
  if (!items.length) return null;

  return (
    <section className="bg-muted/30 py-14 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-sm font-bold uppercase tracking-[0.22em] text-transparent">
              {asString(content.eyebrow, "Feature-by-feature breakdown")}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground lg:text-5xl">{asString(content.heading)}</h2>
            <p className="mt-4 text-lg text-muted-foreground">{asString(content.description)}</p>
          </div>
        </Reveal>
        <div className="grid gap-5 lg:grid-cols-2">
          {items.map((item, index) => (
            <Reveal key={asString(item.title) + index} delay={index * 80}>
              <div className="h-full rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10">
                <IconBubble iconKey={asString(item.iconKey, "Sparkles")} tone={(["default", "violet", "emerald", "amber"] as const)[index % 4]} />
                <h3 className="mt-5 text-xl font-bold text-foreground">{asString(item.title)}</h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">{asString(item.description)}</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
                    <div className="mb-1 font-bold">NotifyStudio</div>
                    {asString((item.notifyStudio as string) || item.bjlistudio)}
                  </div>
                  <div className="rounded-2xl bg-muted p-4 text-sm text-muted-foreground">
                    <div className="mb-1 font-bold text-foreground">{asString(content.competitorName, "Competitor")}</div>
                    {asString(item.competitor)}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SchemaMarkup({ content }: { content: Content }) {
  const schema = content.schema;
  if (!schema || typeof schema !== "object") return null;

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

/* ---------------- dispatcher ---------------- */

export function SaasSection({ sectionKey, content }: { sectionKey: string; content: Content }) {
  if (sectionKey === "saas_hero") return <SaasHero content={content} />;
  if (sectionKey === "feature_story") return <FeatureStory content={content} />;
  if (sectionKey === "impact_stats" || sectionKey === "stats_animated") return <ImpactStats content={content} />;
  if (sectionKey === "agency_scale" || sectionKey === "bento_features") return <AgencyScale content={content} />;
  if (sectionKey === "review_wall") return <ReviewWall content={content} />;
  if (sectionKey === "trusted_by") return <LogoCarousel content={content} />;
  if (sectionKey === "saas_features" || sectionKey === "use_cases" || sectionKey === "integrations_grid" || sectionKey === "benefits")
    return <SaasGrid content={content} columns={sectionKey === "integrations_grid" ? "lg:grid-cols-4" : "lg:grid-cols-3"} />;
  if (sectionKey === "product_demo" || sectionKey === "how_it_works") return <ProductDemo content={content} />;
  if (sectionKey === "pricing_preview" || sectionKey === "pricing_table") return <PricingPreview content={content} />;
  if (sectionKey === "pricing_comparison") return <PricingComparisonTable content={content} />;
  if (sectionKey === "pricing_addons") return <PricingAddons content={content} />;
  if (sectionKey === "pricing_comparison_custom") return <PricingComparison />;
  if (sectionKey === "saas_testimonials") return <TestimonialCards content={content} />;
  if (sectionKey === "saas_cta") return <SaasCta content={content} />;
  if (sectionKey === "comparison_table") return <ComparisonTableSection content={content} />;
  if (sectionKey === "comparison_breakdown") return <ComparisonBreakdown content={content} />;
  if (sectionKey === "schema_markup") return <SchemaMarkup content={content} />;
  return null;
}
